// «Создать ▾» (contracts/ui.md, «Палитра ⌘K и «Создать ▾»»): пункты по правам ролей, у каждого —
// окно создания. Пункт без окна не показывается; нет пунктов — нет кнопки (FR-038а).
// Новое окно создания регистрируется в createModals; окно, которое живёт на своей странице, —
// в createPages: «Протокол» ведёт в «Документы» с окном «Новый протокол» (/documents?upload=new,
// приёмка 0.3.1) — после загрузки документ виден в списке. Клавиша N открывает меню (T051),
// подсказка кнопки её показывает (ux.md, раздел 8); до 640 px кнопка живёт в нижней навигации.
// 014 (T029; FR-032): «Протокол в системе» — окно поверх любой страницы (CreateProtocolModal);
// после создания оно само ведёт в таблицу протокола.
import { Button, Dropdown, DropdownItem, Icon, Tooltip } from '@kp/ui';
import type { ButtonProps } from '@kp/ui';
import { ChevronDown, Plus } from '@kp/ui/icons';
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { CreateProtocolModal } from '../features/documents/CreateProtocolModal.tsx';
import { GrantAccessModal } from '../features/support-access/GrantAccessModal.tsx';
import { NewUserModal } from '../features/users/NewUserModal.tsx';
import { useShell } from './context.tsx';
import type { CreateModalProps, CreateRequest } from './context.tsx';
import { CREATE_SHORTCUT } from './keys.ts';
import { visibleCreateItems } from './permissions.ts';
import type { CreateItem, CreateKind } from './permissions.ts';
import { useMe } from './session.ts';

/** Окна создания по видам. «Выдать доступ» — не пункт «Создать ▾», его открывают палитра и раздел. */
export const createModals: Partial<Record<CreateKind, ComponentType<CreateModalProps>>> = {
  maintainedProtocol: CreateProtocolModal,
  user: NewUserModal,
  supportGrant: GrantAccessModal,
};

/** Окна создания на своей странице: пункт «Создать ▾» ведёт туда. */
export const createPages = {
  document: { to: '/documents', search: { upload: 'new' } },
} as const satisfies Partial<Record<CreateKind, { to: string; search: object }>>;

const availableKinds: ReadonlySet<CreateKind> = new Set([
  ...(Object.keys(createModals) as CreateKind[]),
  ...(Object.keys(createPages) as CreateKind[]),
]);

/** Пункты «Создать ▾» для ролей пользователя — для шапки, палитры и нижней навигации. */
export function useCreateItems(): CreateItem[] {
  return visibleCreateItems(useMe().permissions, availableKinds);
}

/**
 * Кнопка «Создать ▾»: в шапке листа — вторичная, без акцента (README «Каркас экрана»);
 * до 640 px — пункт нижней навигации, меню открывается вверх.
 */
export function CreateMenu({ placement = 'topbar' }: { placement?: 'topbar' | 'bottom' }) {
  const items = useCreateItems();
  const { openCreate, createMenuOpen, setCreateMenuOpen } = useShell();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  if (items.length === 0) return null;
  const trigger =
    placement === 'bottom' ? (
      <button ref={triggerRef} type="button" className="kp-bn-item">
        <Icon icon={Plus} />
        <span>Создать</span>
      </button>
    ) : (
      <CreateButton ref={triggerRef} />
    );
  return (
    <Dropdown
      label="Создать"
      align={placement === 'bottom' ? 'center' : 'end'}
      side={placement === 'bottom' ? 'top' : 'bottom'}
      open={createMenuOpen}
      onOpenChange={setCreateMenuOpen}
      trigger={trigger}
    >
      {items.map((item) => (
        <DropdownItem
          key={item.kind}
          icon={item.icon}
          // Окно открывается после того, как меню вернуло фокус на кнопку: окно запоминает её
          // и возвращает фокус туда же.
          onSelect={() =>
            window.setTimeout(() => {
              if (item.kind === 'document') {
                // Окно откроет страница: фокус уже на кнопке — окно запомнит её.
                triggerRef.current?.focus();
                void navigate(createPages.document);
              } else openCreate(item.kind, { returnFocusRef: triggerRef });
            })
          }
        >
          {item.label}
        </DropdownItem>
      ))}
    </Dropdown>
  );
}

/**
 * «Создать ▾» в шапке листа с подсказкой «Создать N». Принимает обработчики и ref от меню:
 * оно открывается по этой же кнопке.
 */
function CreateButton(props: Omit<ButtonProps, 'children'>) {
  return (
    <Tooltip content="Создать" shortcut={CREATE_SHORTCUT}>
      <Button
        size="sm"
        icon={Plus}
        aria-label="Создать"
        aria-keyshortcuts={CREATE_SHORTCUT}
        className="kp-create"
        {...props}
      >
        <span className="kp-hide-phone">Создать</span>
        <Icon icon={ChevronDown} />
      </Button>
    </Tooltip>
  );
}

/** Открытое окно создания. Закрытое остаётся смонтированным до конца анимации и возврата фокуса. */
export function CreateModals() {
  const { create, closeCreate } = useShell();
  const [last, setLast] = useState<CreateRequest | null>(create);
  if (create !== null && create !== last) setLast(create);
  if (last === null) return null;
  const Modal = createModals[last.kind];
  if (!Modal) return null;
  return (
    <Modal
      key={last.serial}
      open={create !== null}
      onOpenChange={(open) => {
        if (!open) closeCreate();
      }}
      returnFocusRef={last.returnFocusRef}
    />
  );
}
